<script>
    import { onMount } from "svelte";

    import {
        getAmountInCurrency,
        showError,
        showInfo,
    } from "../../../../Utils/utilFunctions.svelte";

    import {
        PatientQuoteBillingStatus as BillingStatus,
        PatientQuoteItems as Items,
        PatientQuoteBillingLatestDetails as LatestBillingDetails,
        PatientQuoteQuotesTable as QuotesTable,
    } from "./components/index.svelte";

    import { unknownPatientInsuranceType } from "../../../../Utils/utilConstants.svelte";

    import {
        billingRuleCalculateTotals,
        getMinHourlyRate,
        getMinTotalFee,
        syncItemsAfterCalculation,
    } from "../../../PatientEncounter/helpers/billingRuleHelper.svelte";

    import { apiMethods } from "../../../../Services/index.svelte";

    import { updateEncounterLatestBillingData } from "../../../PatientEncounter/helpers/encounterHelper.svelte";

    export let patientEncounterDetails = {},
        billingDetails = {},
        latestQuoteData = {},
        doctorProviders = [],
        latestQuoteDataItems = [],
        quotesData = [],
        reloadBillingDetails = () => {},
        isUpdatingBillingStatus = false;

    let isRegenerating = false;

    let quoteItemsRef;

    let __BackupItemsData__ = [];

    onMount(() => {
        __BackupItemsData__ = [...latestQuoteDataItems];
    });

    const regenerateQuote = async (
        obj,
        isRecalculate,
        type,
        isItemChange = false,
        skipRegenerateAndCalculate = false,
    ) => {
        isRegenerating = true;

        if (!type) {
            await reloadBillingDetails();
            isRegenerating = false;
            return;
        }

        if (!skipRegenerateAndCalculate) {
            if (!isRecalculate) await regenerateBilling(obj, type);
            else await calculateAndSyncTotals(obj, isItemChange);

            await reloadBillingDetailsAndUpdateDefaultItems(
                false,
                patientEncounterDetails,
            );
        }

        isRegenerating = false;
    };

    const regenerateBilling = async (obj) => {
        const {
            id: patientEncounterId,
            doctorId,
            patientId,
        } = patientEncounterDetails;

        const pdfObj = {
            entityId: obj.id,
            patientEncounterId,
            doctorId,
            patientId,
            type: 0,
        };

        await regeneratePdfFile(pdfObj);
    };

    const regeneratePdfFile = async (obj) => {
        await apiMethods.postMethod
            .regeneratePdfFile(obj)
            .then((res) => {
                if (!res.patientEncounterId)
                    showError(
                        `Unable to re-generate this ${billingDetailType.toLowerCase()} PDF, please refresh the page and try again and contact support if the issue persists.`,
                        undefined,
                        true,
                    );
            })
            .catch((err) =>
                showError(
                    `Unable to re-generate this ${billingDetailType.toLowerCase()} PDF, please refresh the page and try again and contact support if the issue persists.`,
                    undefined,
                ),
            );
    };

    const calculateAndSyncTotals = async (latestObj, isItemChange) => {
        const {
            billingType,
            usingAmaRates,
            useAsaCodes,
            actualGap,
            calculationAmount,
        } = latestObj;

        const { id: patientEncounterId, doctorId } = patientEncounterDetails;

        if (!billingType || billingType == unknownPatientInsuranceType) return;

        let fundData;

        await apiMethods.getMethod
            .getPatientEncounterFundData(latestObj.id)
            .then((res) => (fundData = res));

        // update mbs items fieldChange in order to calculate totals correctly
        if (!useAsaCodes) {
            const { mbsData } = latestObj;
            const { items } = fundData;

            latestObj.mbsData = mbsData.map((data) => {
                const targetItem = items.find(
                    (item) =>
                        data.itemNum == item.itemIdentifier &&
                        data.subItemNum == item.subItemIdentifier,
                );

                if (targetItem)
                    return {
                        ...data,
                        fieldChange: targetItem.fieldQuantity ?? Number(0),
                    };

                return data;
            });
        }

        let minAmount = await _getItemsTotalMinAmount(latestObj, fundData);

        let requiredAmount = calculationAmount
            ? Math.max(minAmount, calculationAmount)
            : minAmount;

        // when item changes, if rule is "Set unit value" and actualGap is 0, adjust amount - calculated minAmount
        if (
            isItemChange &&
            !useAsaCodes &&
            !usingAmaRates &&
            billingType == 2 &&
            actualGap <= 0 &&
            insuranceType != 30 &&
            getAmountInCurrency(requiredAmount) !=
                getAmountInCurrency(minAmount)
        ) {
            requiredAmount = minAmount;
            showInfo(
                `Unit value updated to ${getAmountInCurrency(requiredAmount)} to maintain 0 for the gap.`,
            );
        }

        const calculateObj = {
            type: billingType == 1 ? 3 : billingType,
            amount: billingType == 1 ? 0 : requiredAmount,
        };

        // sync billingType to calculateObj type
        latestObj.billingType = calculateObj.type;

        // avoid run calculate totals api when no billing rule is selected
        if (
            (calculateObj.type && calculateObj.amount) ||
            (calculateObj.type && calculateObj.type == 3)
        ) {
            // res here would have new property called "usingAmaRates" which needs to be updated in latest billing data object
            let res = await billingRuleCalculateTotals(
                doctorId,
                patientEncounterId,
                latestObj.id,
                calculateObj,
            );

            if (res.error) showError(res.error, undefined, true);
            else {
                latestObj = await syncItemsAfterCalculation(
                    res.resolve,
                    latestObj,
                    fundData,
                );
                latestObj.calculationAmount = requiredAmount;
            }
        }

        // when re-calculate with saved value, might cause data scaling
        if (latestObj.actualGap >= 0)
            await updateEncounterBillingDetails(latestObj);
        else if (latestObj.actualGap > -0.1)
            await updateEncounterBillingDetails(latestObj);
        else
            showError(
                "Invalid gap amount, please refresh the page and try again and contact support if the issue persists.",
                undefined,
                true,
            );
    };

    const updateEncounterBillingDetails = async (obj) => {
        patientEncounterDetails.billingData =
            patientEncounterDetails.billingData.map((bd) => {
                if (bd.id == obj.id) return { ...obj };
                else return bd;
            });

        patientEncounterDetails = updateEncounterLatestBillingData(
            patientEncounterDetails,
        );

        await apiMethods.putMethod
            .updatePatientEncounter(patientEncounterDetails, false, "Update fee estimate billing details")
            .then(async (res) => {
                if (!res)
                    showError(
                        "Failed to update encounter details, please refresh the page and try again and contact support if the issue persists.",
                        undefined,
                        true,
                    );
                else if (res.error) showError(res.error, undefined, true);
                else {
                    await updateQuote(obj);
                    await reloadBillingDetails(false, res);
                }
            })
            .catch((err) => showError(err, undefined, true));
    };

    const updateQuote = async (obj) => {
        const selectedBillingObj = quotesData.find(
            (data) => data.patientEncounterBillingDataId == obj.id,
        );

        if (!selectedBillingObj?.id) return;

        selectedBillingObj.rebate = obj.rebate ?? Number(0);
        selectedBillingObj.actualGap = obj.actualGap ?? Number(0);

        selectedBillingObj.totalFee =
            selectedBillingObj.actualGap +
            selectedBillingObj.rebate +
            (selectedBillingObj.internalProcessingFee
                ? Number(selectedBillingObj.internalProcessingFee)
                : Number(0));

        await apiMethods.putMethod
            .updateEstimate(selectedBillingObj)
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        "Failed to update fee estimate details, please refresh the page and try again and contact support if the issue persists.",
                        undefined,
                        true,
                    );
                else await regenerateBilling(res);
            })
            .catch((err) => showError(err, undefined, true));
    };

    const reloadBillingDetailsAndUpdateDefaultItems = async (
        updateCommunication = false,
        response = {},
    ) => {
        __BackupItemsData__ = [];

        await reloadBillingDetails(updateCommunication, response);

        __BackupItemsData__ = [...latestQuoteDataItems];
    };

    const reloadBillingItemsWhenTimeItemAdded = async () => {
        await quoteItemsRef.updateItems();
    };

    // HELPER FUNCTIONS
    const _getItemsTotalMinAmount = async (obj, fundData) => {
        switch (obj.billingType) {
            case 2:
                return _getMinUnitValue(fundData.items);
            case 3:
                return 0;
            case 4:
                return getMinHourlyRate(fundData, obj.isLatestQuoteData, obj);
            case 5:
                return getMinTotalFee(fundData. obj.billingCodesType == 1, obj.insuranceType);
            case 6:
                return 100;
            default:
                return 0;
        }
    };

    const _getMinUnitValue = (arr = []) => {
        if (!arr || !arr.length) return 0;

        let hfSum = 0,
            unitSum = 0;

        arr.map(({ hfRebate, units }) => {
            hfSum += hfRebate;
            unitSum += units;
        });

        return parseFloat(hfSum / unitSum);
    };
</script>

<div class="relative flex-1">
    <div class="flex flex-col overflow-y-auto absolute inset-0 gap-4 mt-4">
        <BillingStatus {billingDetails} on:reload={reloadBillingDetails} />

        <Items
            latestObjDetails={latestQuoteData}
            itemsData={latestQuoteDataItems}
            doctorProviders={doctorProviders}
            billingData={quotesData}
            {__BackupItemsData__}
            itemType="quote"
            bind:patientEncounterDetails
            bind:isRegenerating
            bind:this={quoteItemsRef}
            {reloadBillingDetails}
            {regenerateQuote}
            {reloadBillingDetailsAndUpdateDefaultItems}
        />

        <LatestBillingDetails
            bind:patientEncounterDetails
            latestObjDetails={latestQuoteData}
            itemsData={latestQuoteDataItems}
            associateArr={quotesData}
            type="Quote"
            {quoteItemsRef}
            {reloadBillingDetails}
            {reloadBillingDetailsAndUpdateDefaultItems}
            {reloadBillingItemsWhenTimeItemAdded}
            {regenerateQuote}
        />

        <QuotesTable
            bind:patientEncounterDetails
            {quotesData}
            {latestQuoteData}
            {latestQuoteDataItems}
            {reloadBillingDetails}
            {reloadBillingDetailsAndUpdateDefaultItems}
        />
    </div>
</div>
